import React from 'react';
import { Heart, Users, Sparkles, TrendingUp } from 'lucide-react';
import { AnimateOnScroll } from './AnimateOnScroll';

/**
 * Section "Ma philosophie / Mon engagement"
 * Met en avant : qualité > quantité, proximité, croissance maîtrisée, passion
 */
export const PhilosophieSection = ({ variant = 'full' }) => {
  const principes = [
    {
      icon: Heart,
      title: 'La qualité avant la quantité',
      description: 'Je préfère accompagner moins de clients, mais mieux. Mon temps n\'étant pas illimité, je le consacre à ceux que je peux vraiment servir avec rigueur et engagement.',
      gradient: 'from-orange-500 to-pink-500',
      bg: 'from-orange-50 to-pink-50'
    },
    {
      icon: Users,
      title: 'Une vraie proximité client',
      description: 'Vous n\'êtes pas un numéro de ticket. Vous avez un interlocuteur unique qui connaît votre entreprise, vos enjeux et votre équipe. Cette relation, c\'est ma signature.',
      gradient: 'from-blue-500 to-cyan-500',
      bg: 'from-blue-50 to-cyan-50'
    },
    {
      icon: TrendingUp,
      title: 'Une croissance maîtrisée',
      description: 'En plein essor, je travaille à agrandir l\'équipe pour mieux gérer la charge — mais sans m\'industrialiser. Garder les bons réflexes du début est non négociable.',
      gradient: 'from-purple-500 to-indigo-500',
      bg: 'from-purple-50 to-indigo-50'
    },
    {
      icon: Sparkles,
      title: 'La passion comme moteur',
      description: 'Grandir avec la même rigueur, la même confiance et le même savoir-faire qu\'au premier jour. Gagner en expérience sans jamais se dénaturer.',
      gradient: 'from-cyan-500 to-blue-500',
      bg: 'from-cyan-50 to-blue-50'
    }
  ];

  return (
    <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-white relative overflow-hidden">
      {/* Décors d'ambiance subtils */}
      <div className="absolute top-20 right-10 w-72 h-72 bg-gradient-to-br from-orange-200/20 to-pink-200/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-20 left-10 w-80 h-80 bg-gradient-to-br from-blue-200/20 to-cyan-200/20 rounded-full blur-3xl pointer-events-none" />

      <div className="mx-auto max-w-6xl relative z-10">
        <AnimateOnScroll>
          <div className="text-center mb-10 sm:mb-12 max-w-3xl mx-auto">
            <span className="inline-block px-4 py-1 bg-gradient-to-r from-orange-100 to-pink-100 text-orange-700 rounded-full text-sm font-semibold mb-4">
              Mon engagement
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              La qualité, la proximité, la passion.
            </h2>
            <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
              {variant === 'full' ? (
                <>
                  Je n'accepte pas tous les clients. Je préfère <strong className="text-orange-600">privilégier la qualité à la quantité</strong> pour 
                  ne faire perdre de temps à personne, ni au client, ni à moi-même. C'est ainsi que je peux garantir 
                  un accompagnement à la hauteur de vos enjeux.
                </>
              ) : (
                <>
                  Je préfère accompagner moins de clients, mais avec un niveau d'exigence et de proximité 
                  que peu peuvent offrir. C'est ma manière de faire la différence.
                </>
              )}
            </p>
          </div>
        </AnimateOnScroll>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
          {principes.map((principe, index) => (
            <AnimateOnScroll key={index} delay={index * 100}>
              <div className={`bg-gradient-to-br ${principe.bg} rounded-2xl p-6 sm:p-8 h-full border border-white shadow-sm hover:shadow-xl transition-all duration-300 group`}>
                <div className="flex items-start space-x-4">
                  <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br ${principe.gradient} flex items-center justify-center shadow-lg flex-shrink-0 transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-300`}>
                    <principe.icon className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">{principe.title}</h3>
                    <p className="text-sm sm:text-base text-gray-700 leading-relaxed">{principe.description}</p>
                  </div>
                </div>
              </div>
            </AnimateOnScroll>
          ))}
        </div>

        {variant === 'full' && (
          <AnimateOnScroll delay={400}>
            <div className="mt-10 sm:mt-12 text-center max-w-3xl mx-auto">
              <div className="inline-block bg-gradient-to-r from-blue-600 via-cyan-600 to-blue-600 p-[2px] rounded-2xl">
                <div className="bg-white rounded-2xl px-6 py-5 sm:px-8 sm:py-6">
                  <p className="text-base sm:text-lg text-gray-800 italic leading-relaxed">
                    « En plein essor, je souhaite grandir avec la <strong className="text-blue-700">même rigueur</strong>, 
                    la <strong className="text-cyan-700">même confiance</strong> et le <strong className="text-blue-700">même savoir-faire</strong> qu'au premier jour. 
                    Gagner en expérience, oui — sans jamais se dénaturer. »
                  </p>
                </div>
              </div>
            </div>
          </AnimateOnScroll>
        )}
      </div>
    </section>
  );
};
